import { useCallback } from 'react'

import { resetAllModifiedDataByKey, useFormField, useGetFormData } from 'cng-common/features/form'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { TKeyInitiative } from 'partner360/entities/keyInitiatives'
import {
  filtersInitialData,
  KEY_INITIATIVE_FILTERS_FORM,
} from 'partner360/features/keyInitiatives/configs/config'
import {
  IKeyInitiativesFiltersForm,
  KeyInitiativesQuickFilter,
} from 'partner360/features/keyInitiatives/model/types'

export const useKeyInitiativesFiltersForm = () => {
  const dispatch = useAppDispatch()

  useGetFormData<IKeyInitiativesFiltersForm>(KEY_INITIATIVE_FILTERS_FORM)

  const { value: keyObjectives, handleChange: changeKeyObjectives } = useFormField<
    IKeyInitiativesFiltersForm['strategicInitiatives']
  >({
    defaultValue: filtersInitialData.strategicInitiatives,
    formKey: KEY_INITIATIVE_FILTERS_FORM,
    field: 'strategicInitiatives',
  })

  const { value: businessPriorities, handleChange: changeBusinessPriorities } = useFormField<
    IKeyInitiativesFiltersForm['businessPriorities']
  >({
    defaultValue: filtersInitialData.businessPriorities,
    formKey: KEY_INITIATIVE_FILTERS_FORM,
    field: 'businessPriorities',
  })

  const { value: statuses, handleChange: changeStatuses } = useFormField<
    IKeyInitiativesFiltersForm['statuses']
  >({
    defaultValue: filtersInitialData.statuses,
    formKey: KEY_INITIATIVE_FILTERS_FORM,
    field: 'statuses',
  })

  const { value: countryCodes, handleChange: changeCountryCodes } = useFormField<
    IKeyInitiativesFiltersForm['countryCodes']
  >({
    defaultValue: filtersInitialData.countryCodes,
    formKey: KEY_INITIATIVE_FILTERS_FORM,
    field: 'countryCodes',
  })

  const { value: keyInitiativeIds, handleChange: changeKeyInitiativeIds } = useFormField<
    IKeyInitiativesFiltersForm['keyInitiativesIds']
  >({
    defaultValue: filtersInitialData.keyInitiativesIds,
    formKey: KEY_INITIATIVE_FILTERS_FORM,
    field: 'keyInitiativesIds',
  })

  const { value: quickFilter, handleChange: changeQuickFilterValue } = useFormField<
    IKeyInitiativesFiltersForm['quickFilter']
  >({
    defaultValue: filtersInitialData.quickFilter,
    formKey: KEY_INITIATIVE_FILTERS_FORM,
    field: 'quickFilter',
  })

  const resetFormData = useCallback(() => {
    dispatch(resetAllModifiedDataByKey(KEY_INITIATIVE_FILTERS_FORM))
  }, [dispatch])

  const changeQuickFilter = useCallback(
    (filter: KeyInitiativesQuickFilter, data: TKeyInitiative[]) => {
      changeKeyInitiativeIds(data.map((el) => el.EntryId))
      changeQuickFilterValue(filter)
    },
    [changeKeyInitiativeIds, changeQuickFilterValue]
  )

  const resetQuickFilter = useCallback(() => {
    changeKeyInitiativeIds(filtersInitialData.keyInitiativesIds)
    changeQuickFilterValue(filtersInitialData.quickFilter)
  }, [changeKeyInitiativeIds, changeQuickFilterValue])

  return {
    resetFormData,
    resetQuickFilter,

    keyObjectives,
    businessPriorities,
    statuses,
    countryCodes,

    keyInitiativeIds,
    quickFilter,

    changeKeyObjectives,
    changeBusinessPriorities,
    changeStatuses,
    changeCountryCodes,
    changeQuickFilter,
  }
}
